import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Layout } from '@/components/layout';
import { Card, EmptyState, ErrorState, Spinner, Button } from '@/components/atoms';
import { toast } from '@/components/atoms/Toast';
import { useBackButton } from '@/hooks';
import { notifications as notificationsApi } from '@/api';
import { useNotificationsStore } from '@/store';
import type { NotificationItem } from '@/types/domain';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

export function NotificationsPage(): JSX.Element {
  useBackButton();
  const [items, setItems] = useState<NotificationItem[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const setUnread = useNotificationsStore((s) => s.setUnread);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);

  const load = async (): Promise<void> => {
    setError(null);
    try {
      const data = await notificationsApi.fetchNotifications();
      setItems(data.results);
      // F-160 D1: keep the header bell badge in sync with what the user sees.
      setUnread(data.results.filter((n) => n.read_at === null).length);
    } catch (err) {
      logger.warn('Notifications fetch failed', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setItems(null);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const readAll = async (): Promise<void> => {
    try {
      await notificationsApi.markAllNotificationsRead();
      markAllRead();
      void load();
    } catch (err) {
      logger.warn('markAllNotificationsRead failed', err);
      toast.error('Не вдалось позначити прочитаними');
    }
  };

  if (error)
    return (
      <Layout title="Сповіщення">
        <ErrorState onRetry={() => void load()} />
      </Layout>
    );
  if (items === null) return <Layout title="Сповіщення"><Spinner center /></Layout>;
  if (items.length === 0)
    return (
      <Layout title="Сповіщення">
        <EmptyState
          icon="bell"
          message="Поки тиша. Як з'явиться щось важливе — побачиш тут."
          action={{ label: 'На головну', to: '/' }}
        />
      </Layout>
    );

  return (
    <Layout
      title="Сповіщення"
      headerRight={
        <Button variant="ghost" compact onClick={readAll}>
          Все прочитано
        </Button>
      }
    >
      <div className={styles.list}>
        {items.map((n) => (
          <Card key={n.id}>
            <strong>{n.title}</strong>
            <p className={styles.note}>{n.body}</p>
            <p className={styles.note}>{dayjs(n.created_at).format('D MMM HH:mm')}</p>
          </Card>
        ))}
      </div>
    </Layout>
  );
}
